/**
 * The officially assigned ISO 3166-1 alpha-2 country codes, and the canonical form of a
 * country value.
 *
 * Mirrors LENS-ARS `CountryCodes` (BusinessLogic/Profiles/CountryCodes.cs), which rejects any
 * other value with `400 VALIDATION_FAILED`; keep the two lists in step. Like ARS, the list is
 * held in code rather than derived from the runtime's locale data, which varies by platform.
 */

export const ISO_COUNTRY_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS',
  'BT', 'BV', 'BW', 'BY', 'BZ',
  'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN', 'CO', 'CR', 'CU', 'CV', 'CW',
  'CX', 'CY', 'CZ',
  'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ',
  'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET',
  'FI', 'FJ', 'FK', 'FM', 'FO', 'FR',
  'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT',
  'GU', 'GW', 'GY',
  'HK', 'HM', 'HN', 'HR', 'HT', 'HU',
  'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT',
  'JE', 'JM', 'JO', 'JP',
  'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ',
  'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY',
  'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS',
  'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ',
  'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ',
  'OM',
  'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY',
  'QA',
  'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
  'ST', 'SV', 'SX', 'SY', 'SZ',
  'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ',
  'UA', 'UG', 'UM', 'US', 'UY', 'UZ',
  'VA', 'VC', 'VE', 'VG', 'VI', 'VN', 'VU',
  'WF', 'WS',
  'YE', 'YT',
  'ZA', 'ZM', 'ZW',
] as const;

export type IsoCountryCode = (typeof ISO_COUNTRY_CODES)[number];

const ASSIGNED: ReadonlySet<string> = new Set(ISO_COUNTRY_CODES);

/** The canonical (trimmed, upper-case) form of a country value, as ARS stores it. */
export function normalizeCountry(country: string): string {
  return country.trim().toUpperCase();
}

/** Whether a value is an assigned ISO 3166-1 alpha-2 code, after normalization. */
export function isAssignedCountry(country: string): boolean {
  return ASSIGNED.has(normalizeCountry(country));
}

// Names are display-only, so taking them from the runtime is fine; validation never does.
const regionNames =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region', fallback: 'code' })
    : null;

/** The English name of an assigned code, or the code itself if the runtime has none. */
export function countryName(code: string): string {
  return regionNames?.of(code) ?? code;
}

export interface CountryOption {
  code: IsoCountryCode;
  name: string;
}

/** Every assigned code with its English name, in code order. */
export const COUNTRY_OPTIONS: readonly CountryOption[] = ISO_COUNTRY_CODES.map((code) => ({
  code,
  name: countryName(code),
}));

/**
 * The options matching what has been typed: codes starting with it first (so "us" offers
 * US before Australia), then names containing it. Blank shows everything.
 */
export function filterCountryOptions(query: string): CountryOption[] {
  const trimmed = query.trim();
  if (trimmed === '') {
    return [...COUNTRY_OPTIONS];
  }

  const upper = trimmed.toUpperCase();
  const lower = trimmed.toLowerCase();
  const byCode = COUNTRY_OPTIONS.filter((option) => option.code.startsWith(upper));
  const byName = COUNTRY_OPTIONS.filter(
    (option) => !option.code.startsWith(upper) && option.name.toLowerCase().includes(lower),
  );
  return [...byCode, ...byName];
}
